import { useState } from 'react';
import { Card, CardContent, CardHeader } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { useToast } from '@/components/ui/Toast';
import { usePermissions, PERMISSIONS } from '@/hooks/usePermissions';
import { GroupMemberJobFanout } from './GroupMemberJobFanout';
import { useCreateGroupJobs, type JobDetail, type GroupJobsResult } from './hooks/useJobs';

function summarize(result: GroupJobsResult): string {
  const parts: string[] = [];
  if (result.successful > 0) parts.push(`created ${result.successful}`);
  if (result.skipped > 0) parts.push(`skipped ${result.skipped} (unfinished job exists)`);
  if (result.failed > 0) parts.push(`${result.failed} failed`);
  return parts.length > 0 ? parts.join(', ') : 'no changes';
}

/**
 * JobGroupsTab — shown on the Job Detail page. Lists the group members of this
 * job's client and lets the user trigger the SAME job for selected members
 * (each becomes a new individual job). All actions are permission-gated:
 *  - GROUPS_READ gates the tab itself (enforced by the parent).
 *  - JOBS_CREATE gates the ability to select members and trigger jobs; without
 *    it the section is read-only (members + their open jobs are still visible).
 */
export function JobGroupsTab({ job }: { job: JobDetail }) {
  const { can } = usePermissions();
  const { toast } = useToast();
  const canCreate = can(PERMISSIONS.JOBS_CREATE);

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [duplicateIds, setDuplicateIds] = useState<string[]>([]);
  const createGroupJobs = useCreateGroupJobs();

  const handleCreate = async () => {
    if (selectedIds.length === 0) return;

    let allowDuplicates = false;
    if (duplicateIds.length > 0) {
      allowDuplicates = window.confirm(
        `${duplicateIds.length} selected member(s) already have an unfinished job of this type.\n\n` +
          `Click OK to create the job for them anyway, or Cancel to skip those members.`,
      );
    }

    try {
      const result = await createGroupJobs.mutateAsync({
        clientIds: selectedIds,
        allowDuplicates,
        entityTypeId: job.entityTypeId || undefined,
        practiceEntityId: job.practiceEntityId || undefined,
        jobType: job.jobType || undefined,
        workflowTemplateId: job.workflowTemplateId || undefined,
        title: job.title,
        description: job.description || undefined,
        priority: job.priority,
        assignedPartnerId: job.assignedPartnerId || undefined,
        assignedManagerId: job.assignedManagerId || undefined,
        assignedAccountantId: job.assignedAccountantId || undefined,
        budgetAmount: job.budgetAmount ?? undefined,
        estimatedHours: job.estimatedHours ?? undefined,
      });
      toast({
        variant: result.failed > 0 ? 'warning' : 'success',
        title: 'Group member jobs',
        description: summarize(result),
      });
      setSelectedIds([]);
    } catch {
      toast({ variant: 'error', title: 'Failed to create jobs for group members' });
    }
  };

  return (
    <Card>
      <CardHeader
        title="Group Members"
        description={
          canCreate
            ? `Create this job ("${job.title}") for members of this client's group(s). Existing open jobs are shown so you can avoid duplicates.`
            : "Members of this client's group(s) and their open jobs."
        }
        action={
          canCreate ? (
            <Button
              size="sm"
              onClick={handleCreate}
              disabled={selectedIds.length === 0}
              loading={createGroupJobs.isPending}
            >
              Create job for {selectedIds.length || 'selected'}{' '}
              {selectedIds.length === 1 ? 'member' : 'members'}
            </Button>
          ) : undefined
        }
      />
      <CardContent>
        <GroupMemberJobFanout
          clientId={job.clientId}
          selectedIds={canCreate ? selectedIds : []}
          onChange={canCreate ? setSelectedIds : () => {}}
          onDuplicatesChange={setDuplicateIds}
          jobType={job.jobType}
          readOnly={!canCreate}
          hideHeader
        />
      </CardContent>
    </Card>
  );
}
